/**
 * `campfirePlanType.ts` — port of Unity's `CampfirePlanType` and the two
 * derived predicates on `CustomerData` / `ProjectInfo`.
 *
 * ⚠ THE VALUES ARE NOT SEQUENTIAL AND DO NOT START AT 0
 * (`Models/Data/CustomerData.cs:15-22`). Re-deriving them as an ordinal list
 * silently mislabels every account.
 */
export const CAMPFIRE_PLAN_TYPE = {
  Unknown: -1,
  EnterpriseFree: 1,
  EnterprisePaid: 2,
  IndividualFree: 3,
  IndividualPaid: 4,
} as const;

/**
 * `ProjectInfo.IsProjectPaid` (`ProjectInfo.cs:161-168`) and
 * `CustomerData.IsPaidAccount` (`:83-91`).
 *
 * ⚠ **`EnterpriseFree` counts as PAID.** That asymmetry is upstream's, it is
 * deliberate, and it is the single easiest thing to re-derive wrongly here —
 * an "enterprise free" account is a seat-less enterprise tenant, not a
 * consumer free tier. `IsLicenseAssigned` is the predicate that excludes it;
 * this one does not.
 */
export function isPaidPlan(planType: number | undefined | null): boolean {
  return planType === CAMPFIRE_PLAN_TYPE.EnterprisePaid
    || planType === CAMPFIRE_PLAN_TYPE.IndividualPaid
    || planType === CAMPFIRE_PLAN_TYPE.EnterpriseFree;
}

/** Tolerant read: the GraphQL field is an int, but be forgiving of a string. */
export function parsePlanType(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
    switch (value) {
      case "EnterpriseFree": return CAMPFIRE_PLAN_TYPE.EnterpriseFree;
      case "EnterprisePaid": return CAMPFIRE_PLAN_TYPE.EnterprisePaid;
      case "IndividualFree": return CAMPFIRE_PLAN_TYPE.IndividualFree;
      case "IndividualPaid": return CAMPFIRE_PLAN_TYPE.IndividualPaid;
      default: break;
    }
  }
  return CAMPFIRE_PLAN_TYPE.Unknown;
}
